import { Button, Select, SelectItem } from '@mantine/core';
import { useMemo, useState } from 'react';
import { INextAvailableAppointment } from '../../../hooks/promises/getNextAvailableAppointments';

interface IAvailableAppointmentsMenuEditingProps {
  availabilityOptions: INextAvailableAppointment[];
  nextAvailableAppointmentOption: INextAvailableAppointment | undefined;
  setEditing: React.Dispatch<React.SetStateAction<boolean>>;
  addUserNextAvailableAppointment: (
    nextAvailableAppointmentId: number
  ) => Promise<void>;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => void;
  thisMonthYear: string;
}

function AvailableAppointmentsMenuEditing({
  availabilityOptions,
  nextAvailableAppointmentOption,
  setEditing,
  addUserNextAvailableAppointment,
  setSaving,
  refetch,
  thisMonthYear,
}: IAvailableAppointmentsMenuEditingProps) {
  const menuOptions: SelectItem[] = useMemo(() => {
    return availabilityOptions.map((item) => {
      return {
        value: item.nextAvailableAppointmentId.toString(),
        label: item.nextAvailableAppointmentDescription,
      };
    });
  }, [availabilityOptions]);

  const [selectedAvailabilityOption, setSelectedAvailabilityOption] = useState(
    nextAvailableAppointmentOption
  );

  const optionHasChanged = useMemo(() => {
    return nextAvailableAppointmentOption !== selectedAvailabilityOption;
  }, [selectedAvailabilityOption, nextAvailableAppointmentOption]);

  const onChange = (value: string | null) => {
    if (value) {
      const selectedOption = availabilityOptions.find(
        (option) => option.nextAvailableAppointmentId === Number(value)
      );
      if (selectedOption) {
        setSelectedAvailabilityOption(selectedOption);
      }
    }
  };

  const onClickSave = () => {
    if (selectedAvailabilityOption) {
      setSaving(true);
      addUserNextAvailableAppointment(
        selectedAvailabilityOption?.nextAvailableAppointmentId
      )
        .then(() => {
          refetch();
        })
        .then(() => {
          setSaving(false);
        });
    }
    setEditing(false);
  };

  return (
    <div className="flex flex-col items-end gap-4">
      <Select
        label={`${thisMonthYear}: Next Available Appointment`}
        data={menuOptions}
        onChange={onChange}
        size="lg"
        value={selectedAvailabilityOption?.nextAvailableAppointmentId.toString()}
        className="w-full"
      />
      <div className="flex gap-2">
        <div
          title={optionHasChanged ? 'Save changes' : 'No changes to save'}
          className={` ${
            optionHasChanged ? 'cursor-pointer' : 'cursor-not-allowed'
          } `}
        >
          <Button
            className="font-semibold text-white border-2 bg-primary-500 hover:bg-primary-600 hover:border-primary-600 border-primary-500"
            onClick={onClickSave}
            disabled={!optionHasChanged}
          >
            Save
          </Button>
        </div>
        <Button
          className="bg-red-600 hover:bg-red-700"
          onClick={() => setEditing(false)}
          title="Cancel editing"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default AvailableAppointmentsMenuEditing;
